// ============================================================================
// OBT F2-A — Kampanya liste ekranı
// ============================================================================
// Gerçek GET /obt/campaigns verisi. Filtreler yalnız backend'in desteklediği
// alanlardır (durum, konu). Sahte veri yok; sayfalama backend zarfıyla uyumlu.
// Durum etiketleri Türkçe, gerçek enum değeri korunur (ObtStatusBadge).
// ============================================================================

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEffect, useState } from 'react';
import {
  Plus,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  FileEdit,
  CalendarClock,
  CalendarPlus,
  Archive,
  ArchiveRestore,
  Trash2,
  Undo2,
} from 'lucide-react';
import {
  ObtPageHeader,
  ObtLoading,
  ObtErrorState,
  ObtEmptyState,
  ObtStatusBadge,
} from '../ObtStates';
import { campaignPhase } from '../obtEnums';
import { useCampaignList } from './useCampaignList';
import { restoreCampaign, fetchObtHardDeleteEnabled } from './campaignApi';
import ExtendDueDialog from './ExtendDueDialog';
import CampaignTargetsDialog from './CampaignTargetsDialog';
import ArchiveCampaignDialog from './ArchiveCampaignDialog';
import WithdrawCampaignDialog from './WithdrawCampaignDialog';
import DeleteCampaignDialog from './DeleteCampaignDialog';
import HardDeleteCampaignDialog from './HardDeleteCampaignDialog';

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Tüm durumlar' },
  { value: 'draft', label: 'Taslak' },
  { value: 'collecting_responses', label: 'Cevap Toplanıyor' },
  { value: 'under_review', label: 'İncelemede' },
  { value: 'director_review', label: 'Müdür İncelemesinde' },
  { value: 'decided', label: 'Karara Bağlandı' },
  { value: 'completed', label: 'Tamamlandı' },
  { value: 'cancelled', label: 'İptal Edildi' },
];

function formatDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString('tr-TR', {
    timeZone: 'Europe/Istanbul',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Son-cevap tarihi: gün adı + "saat" ile ayrıntılı gösterim (istenen biçim:
// "14 Ağustos 2026 Cuma saat 12:20"). Sıra locale'den bağımsız garanti edilsin
// diye parçalardan kurulur; hepsi Europe/Istanbul'a göre.
function formatDueDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const parts = new Intl.DateTimeFormat('tr-TR', {
    timeZone: 'Europe/Istanbul',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(parsed);
  const get = (type) => parts.find((p) => p.type === type)?.value || '';
  return `${get('day')} ${get('month')} ${get('year')} ${get('weekday')} saat ${get('hour')}:${get('minute')}`;
}

function CampaignRow({
  campaign,
  onEditDraft,
  onExtendDue,
  onWithdraw,
  onShowTargets,
  onArchive,
  onRestore,
  onDelete,
  onHardDelete,
  isArchivedView = false,
  isDeletedView = false,
  restoring = false,
}) {
  const due = formatDueDate(campaign?.response_due_at);
  const created = formatDate(campaign?.created_at);
  const archivedAt = formatDate(campaign?.archived_at);
  const purgedAt = formatDate(campaign?.purged_at);
  const purgedBy = campaign?.purged_by?.name;
  const purgeReason = campaign?.purge_reason;
  const creator = campaign?.created_by?.name;
  const isDraft = campaign?.status === 'draft';
  // Faz 1 — YÖNETİM YETKİSİ (backend `can_manage`): SAHİP (oluşturan ∨ yayınlayan)
  // ∨ admin. Ast, üstünün kampanyasını GÖRÜR ama yönet/sil butonlarını GÖRMEZ.
  // Fail-closed: bayrak yoksa buton çıkmaz. (Nihai yetki her hâlde sunucuda.)
  const canManage = campaign?.can_manage === true;
  // Cevap toplanan bilgi talebinde son tarih BELİRLENEBİLİR (kurulumda unutulmuşsa)
  // veya UZATILABİLİR. Nihai yetki/kural (yalnız ileri tarih) sunucudadır.
  const hasDue = Boolean(campaign?.response_due_at);
  const canExtendThis =
    canManage &&
    !isArchivedView &&
    Boolean(onExtendDue) &&
    campaign?.status === 'collecting_responses';
  // Geri çek: yalnız cevap toplanan (yeni yayınlanmış) talep; nihai kural (0 cevap)
  // sunucuda. Yanlış/eksik kurulmuş yayını taslağa indirip düzeltmeye yarar.
  const canWithdrawThis =
    canManage &&
    !isArchivedView &&
    !isDeletedView &&
    Boolean(onWithdraw) &&
    campaign?.status === 'collecting_responses';
  const targetCount = campaign?.target_count ?? 0;
  const delegateCount = campaign?.delegate_count ?? 0;
  const answerCount = campaign?.submitted_response_count ?? 0;
  return (
    <li className="rounded-lg border border-gray-200 p-3.5 transition hover:border-gray-300 hover:shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-2">
          {/* Başlık + durum */}
          <div className="flex flex-wrap items-center gap-2">
            {onShowTargets ? (
              <button
                type="button"
                onClick={() => onShowTargets(campaign)}
                className="truncate rounded text-sm font-semibold text-gray-900 hover:text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                title="Detayları ve hedefleri gör"
              >
                {campaign?.title || 'Başlıksız bilgi talebi'}
              </button>
            ) : (
              <span className="truncate text-sm font-semibold text-gray-900">
                {campaign?.title || 'Başlıksız bilgi talebi'}
              </span>
            )}
            <ObtStatusBadge {...campaignPhase(campaign)} />
          </div>

          {/* Meta bilgiler */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
            {campaign?.topic && (
              <span>
                Konu:{' '}
                <span className="font-medium text-gray-700">{campaign.topic}</span>
              </span>
            )}
            {creator && (
              <span>
                Oluşturan: <span className="text-gray-700">{creator}</span>
              </span>
            )}
            {created && <span>Oluşturma: {created}</span>}
            {due && <span>Son cevap: {due}</span>}
            {isArchivedView && archivedAt && <span>Arşivlendi: {archivedAt}</span>}
          </div>

          {/* Sayaç rozetleri */}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {onShowTargets ? (
              <button
                type="button"
                onClick={() => onShowTargets(campaign)}
                className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 font-medium text-blue-700 ring-1 ring-inset ring-blue-200 transition hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                title="Hedeflenen kişileri ve cevap durumlarını gör"
              >
                {targetCount} hedef
              </button>
            ) : (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 font-medium text-gray-600">
                {targetCount} hedef
              </span>
            )}
            {delegateCount > 0 && (
              <span
                className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 font-medium text-amber-700 ring-1 ring-inset ring-amber-200"
                title="Doğrudan hedeflenen üst amir(ler) — Bölge Mühendisi/Müdür Yardımcısı. Kendileri cevaplamaz; kendi panelindeki “Dağıtım Görevleri”nden Formenlerine dağıtır. Dağıtımdan sonra hedef sayısı artar."
              >
                {delegateCount} dağıtıcı
                {targetCount === 0 ? ' · dağıtım bekliyor' : ''}
              </span>
            )}
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 font-medium text-gray-600">
              {answerCount} cevap
            </span>
          </div>

          {isDeletedView && (
            <p className="text-xs text-red-700">
              Silindi
              {purgedBy ? ` · ${purgedBy}` : ''}
              {purgedAt ? ` · ${purgedAt}` : ''}
              {purgeReason ? ` · Gerekçe: ${purgeReason}` : ''}
            </p>
          )}
        </div>

        <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
        {isDeletedView ? (
          onHardDelete && canManage && (
            <Button
              variant="outline"
              size="sm"
              className="border-red-300 text-red-700 hover:bg-red-50"
              onClick={() => onHardDelete(campaign)}
              title="Kalıcı (fiziksel) sil — geri alınamaz"
            >
              <Trash2 className="mr-1 h-4 w-4" aria-hidden="true" />
              Tam Sil
            </Button>
          )
        ) : isArchivedView ? (
          <>
            {onRestore && canManage && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRestore(campaign)}
                disabled={restoring}
              >
                <ArchiveRestore className="mr-1 h-4 w-4" aria-hidden="true" />
                Geri Al
              </Button>
            )}
            {onDelete && canManage && (
              <Button
                variant="outline"
                size="sm"
                className="border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => onDelete(campaign)}
                title="Bilgi talebini kalıcı sil (künye kalır)"
              >
                <Trash2 className="mr-1 h-4 w-4" aria-hidden="true" />
                Kalıcı Sil
              </Button>
            )}
          </>
        ) : (
          <>
            {isDraft && onEditDraft && canManage && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEditDraft(campaign.campaign_id)}
              >
                <FileEdit className="mr-1 h-4 w-4" aria-hidden="true" />
                Taslağı Düzenle
              </Button>
            )}
            {canExtendThis && hasDue && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onExtendDue(campaign)}
              >
                <CalendarClock className="mr-1 h-4 w-4" aria-hidden="true" />
                Süreyi Uzat
              </Button>
            )}
            {canExtendThis && !hasDue && (
              // Son tarih UNUTULMUŞ: dolu amber buton — kalabalık aksiyonlar
              // arasında dikkat çeker (outline "Süreyi Uzat"tan görsel olarak ayrık).
              <Button
                size="sm"
                className="bg-amber-500 text-white hover:bg-amber-600 focus-visible:ring-amber-500"
                onClick={() => onExtendDue(campaign)}
                title="Bu bilgi talebinde son cevap tarihi belirlenmemiş"
              >
                <CalendarPlus className="mr-1 h-4 w-4" aria-hidden="true" />
                Son Tarih Belirle
              </Button>
            )}
            {canWithdrawThis && (
              <Button
                variant="outline"
                size="sm"
                className="text-amber-700 hover:text-amber-900"
                onClick={() => onWithdraw(campaign)}
                title="Yayını geri çek, taslağa döndür ve yeniden düzenle (yalnız cevap alınmamışsa)"
              >
                <Undo2 className="mr-1 h-4 w-4" aria-hidden="true" />
                Geri Çek
              </Button>
            )}
            {onArchive && canManage && (
              <Button
                variant="outline"
                size="sm"
                className="text-gray-600 hover:text-gray-900"
                onClick={() => onArchive(campaign)}
                title="Bilgi talebini arşivle (geri alınabilir)"
              >
                <Archive className="mr-1 h-4 w-4" aria-hidden="true" />
                Arşivle
              </Button>
            )}
          </>
        )}
        </div>
      </div>
    </li>
  );
}

export default function CampaignListView({
  enabled = true,
  onNewCampaign,
  onEditDraft,
  canExtend = false,
  onBack = null,
}) {
  const {
    loading,
    error,
    items,
    page,
    filters,
    offset,
    setOffset,
    applyFilters,
    reload,
    pageSize,
  } = useCampaignList({ enabled });

  // Süre uzatma diyaloğu için seçili kampanya (null → kapalı).
  const [extendTarget, setExtendTarget] = useState(null);
  // Hedef listesi diyaloğu için seçili kampanya (null → kapalı).
  const [targetsCampaign, setTargetsCampaign] = useState(null);
  // Arşivleme onay diyaloğu için seçili kampanya (null → kapalı).
  const [archiveTarget, setArchiveTarget] = useState(null);
  // Geri çekme (taslağa döndür) onay diyaloğu için seçili kampanya (null → kapalı).
  const [withdrawTarget, setWithdrawTarget] = useState(null);
  // Kalıcı silme (belgeli) onay diyaloğu için seçili kampanya (null → kapalı).
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Fiziksel tam silme diyaloğu için seçili kampanya (null → kapalı).
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null);
  // Admin sistem ayarı: fiziksel tam silme açık mı? (Silinenler'de "Tam Sil").
  const [hardDeleteEnabled, setHardDeleteEnabled] = useState(false);
  // Geri-alma sırasında butonu kilitlemek için (kampanya id'si).
  const [restoringId, setRestoringId] = useState(null);

  const isArchivedView = Boolean(filters.archived);
  const isDeletedView = Boolean(filters.deleted);

  // Silinenler görünümüne girince, "Tam Sil"in gösterilip gösterilmeyeceğini
  // belirleyen admin sistem ayarını oku (kapalıysa düğme hiç çıkmaz).
  useEffect(() => {
    if (!isDeletedView) return undefined;
    let active = true;
    fetchObtHardDeleteEnabled().then((on) => {
      if (active) setHardDeleteEnabled(on);
    });
    return () => {
      active = false;
    };
  }, [isDeletedView]);

  const handleRestore = async (campaign) => {
    if (!campaign?.campaign_id) return;
    setRestoringId(campaign.campaign_id);
    try {
      await restoreCampaign(campaign.campaign_id);
      reload();
    } finally {
      setRestoringId(null);
    }
  };

  const total = page?.total ?? 0;
  const hasPrev = offset > 0;
  const hasNext = page?.has_more ?? false;

  return (
    <div className="space-y-5">
      <ObtPageHeader
        onBack={onBack}
        title="Bilgi Talepleri"
        description="Kapsamınızdaki bilgi taleplerini görüntüleyin ve yeni bilgi talebi oluşturun."
        actions={
          <div className="flex flex-wrap gap-2">
            {/* Aktif / Arşiv / Silinenler görünüm geçişi */}
            <div className="inline-flex overflow-hidden rounded-md border border-gray-200">
              <Button
                type="button"
                variant={!isArchivedView && !isDeletedView ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none border-0"
                onClick={() => applyFilters({ archived: false, deleted: false })}
                aria-pressed={!isArchivedView && !isDeletedView}
              >
                Aktif
              </Button>
              <Button
                type="button"
                variant={isArchivedView ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none border-0"
                onClick={() => applyFilters({ archived: true, deleted: false })}
                aria-pressed={isArchivedView}
              >
                <Archive className="mr-1.5 h-4 w-4" aria-hidden="true" />
                Arşiv
              </Button>
              <Button
                type="button"
                variant={isDeletedView ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none border-0"
                onClick={() => applyFilters({ archived: false, deleted: true })}
                aria-pressed={isDeletedView}
              >
                <Trash2 className="mr-1.5 h-4 w-4" aria-hidden="true" />
                Silinenler
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={reload} disabled={loading}>
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
              Yenile
            </Button>
            {onNewCampaign && !isArchivedView && !isDeletedView && (
              <Button size="sm" onClick={onNewCampaign}>
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Yeni Bilgi Talebi
              </Button>
            )}
          </div>
        }
      />

      {/* Filtreler — yalnız backend destekli alanlar */}
      <Card>
        <CardContent className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="f-status">Durum</Label>
            <Select
              value={filters.status || '__all'}
              onValueChange={(v) =>
                applyFilters({ status: v === '__all' ? '' : v })
              }
            >
              <SelectTrigger id="f-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FILTER_OPTIONS.map((o) => (
                  <SelectItem key={o.value || '__all'} value={o.value || '__all'}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="f-topic">Konu</Label>
            <Input
              id="f-topic"
              value={filters.topic}
              placeholder="Konuya göre filtrele"
              onChange={(e) => applyFilters({ topic: e.target.value })}
            />
          </div>
        </CardContent>
      </Card>

      {/* Liste */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            {isDeletedView
              ? total > 0
                ? `Silinenler · ${total} bilgi talebi`
                : 'Silinenler'
              : isArchivedView
                ? total > 0
                  ? `Arşiv · ${total} bilgi talebi`
                  : 'Arşiv'
                : total > 0
                  ? `${total} bilgi talebi`
                  : 'Bilgi Talepleri'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading && <ObtLoading label="Bilgi talepleri yükleniyor…" />}
          {!loading && error && <ObtErrorState error={error} onRetry={reload} />}
          {!loading && !error && items.length === 0 && (
            <ObtEmptyState
              title={
                isDeletedView
                  ? 'Silinmiş bilgi talebi yok'
                  : isArchivedView
                    ? 'Arşiv boş'
                    : 'Bilgi talebi bulunmuyor'
              }
              description={
                isDeletedView
                  ? 'Kalıcı silinmiş bilgi talebi yok. Bir bilgi talebini arşivden kalıcı sildiğinizde, kim/ne zaman/neden sildiği künyesiyle burada görünür.'
                  : isArchivedView
                    ? 'Arşivlenmiş bilgi talebi yok. Bir bilgi talebini arşivlediğinizde burada görünür ve geri alabilirsiniz.'
                    : 'Seçilen filtrelerle eşleşen bilgi talebi yok. Yeni bir bilgi talebi oluşturabilirsiniz.'
              }
            />
          )}
          {!loading && !error && items.length > 0 && (
            <ul className="space-y-3">
              {items.map((c) => (
                <CampaignRow
                  key={c.campaign_id}
                  campaign={c}
                  onEditDraft={onEditDraft}
                  onExtendDue={canExtend ? setExtendTarget : null}
                  onWithdraw={
                    canExtend && !isArchivedView && !isDeletedView
                      ? setWithdrawTarget
                      : null
                  }
                  onShowTargets={setTargetsCampaign}
                  isArchivedView={isArchivedView}
                  isDeletedView={isDeletedView}
                  onArchive={isArchivedView || isDeletedView ? null : setArchiveTarget}
                  onRestore={isArchivedView ? handleRestore : null}
                  onDelete={isArchivedView ? setDeleteTarget : null}
                  onHardDelete={
                    isDeletedView && hardDeleteEnabled ? setHardDeleteTarget : null
                  }
                  restoring={restoringId === c.campaign_id}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Sayfalama */}
      {!loading && !error && (hasPrev || hasNext) && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            disabled={!hasPrev}
            onClick={() => setOffset(Math.max(0, offset - pageSize))}
          >
            <ChevronLeft className="mr-1 h-4 w-4" aria-hidden="true" />
            Önceki
          </Button>
          <span className="text-xs text-gray-500">
            {offset + 1}–{offset + items.length} / {total}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasNext}
            onClick={() => setOffset(offset + pageSize)}
          >
            Sonraki
            <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      )}

      <ExtendDueDialog
        open={Boolean(extendTarget)}
        campaign={extendTarget}
        onClose={() => setExtendTarget(null)}
        onExtended={reload}
      />

      <CampaignTargetsDialog
        open={Boolean(targetsCampaign)}
        campaign={targetsCampaign}
        onClose={() => setTargetsCampaign(null)}
      />

      <ArchiveCampaignDialog
        open={Boolean(archiveTarget)}
        campaign={archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onArchived={reload}
      />

      <WithdrawCampaignDialog
        open={Boolean(withdrawTarget)}
        campaign={withdrawTarget}
        onClose={() => setWithdrawTarget(null)}
        onWithdrawn={(campaignId, previousTargetIds) => {
          // Geri çekme başarılı → taslağa indi. Kullanıcıyı doğrudan düzenleyiciye
          // götür (yoksa listeyi tazele). "Geri çekip düzenle" akışı tek adımda tamam.
          // Önceki hedefler düzenleyiciye taşınır → TargetPicker'da önceden seçili.
          if (onEditDraft) onEditDraft(campaignId, previousTargetIds);
          else reload();
        }}
      />

      <DeleteCampaignDialog
        open={Boolean(deleteTarget)}
        campaign={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={reload}
      />

      <HardDeleteCampaignDialog
        open={Boolean(hardDeleteTarget)}
        campaign={hardDeleteTarget}
        onClose={() => setHardDeleteTarget(null)}
        onDeleted={reload}
      />
    </div>
  );
}
